import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import i18n from 'i18next';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import CommonButton from '@src/new-components/Button';
import Typography from '@src/new-components/Typography';
import DropdownCustom from '@src/new-components/Dropdown';
import Tooltip from '@src/components/Tooltip';
import actions from '@src/redux/actions';
import { COLOR } from '@src/styles/color';
import { LANGUAGE } from '@src/constants';
import { HISTORY_SCOPE } from '@src/constants/history';
import useTotalCredits from '@src/hooks/useTotalCredits';
import useIsAutoSaveOverLimit from '@src/hooks/useIsAutoSaveOverLimit';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import apis from '@src/apis';
import {
  calculateTranslateCredits,
  handleTranslateProject,
} from '@src/services/project';
import { selectHasSynthesizingBlock } from '@src/redux/project/selectors';
import { StyledTranslateContent, StyledTranslateActions } from './index.style';

const TranslateDialog = ({
  open,
  onClose,
  projectId,
  blocks = [], // Array of block objects
  findBlockWithError,
  scrollToBlock,
  onTranslateLoadingChange,
  onTranslateError,
  onOpenPopupNotEnoughCredits,
  onSaveProject, // Callback to save project before translating
  computeBlockBlackWordsMap,
  setBlockBlackWordsMap,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  // Over the character limit the project can't be auto-saved, so block translate.
  const isAutoSaveOverLimit = useIsAutoSaveOverLimit();
  const { language: currentLang } = i18n;
  const { totalCredits } = useTotalCredits();
  const { user } = useSelector((state) => state.auth);
  const { getFeatureValue } = useFeatureFlags();
  const undoRedoEnabled = getFeatureValue(
    FEATURE_KEYS.PROJECT_DUBBING_UNDO_REDO,
    { userId: user?.id, email: user?.email, phoneNumber: user?.phoneNumber },
  );
  const translateSupportedLanguages = getFeatureValue(
    FEATURE_KEYS.TRANSLATE_SUPPORTED_LANGUAGES,
    { userId: user?.id, email: user?.email },
  );
  const { languagesV3 } = useSelector((state) => state.language);
  const multiBlock = useSelector((state) => state.project?.multiBlock);
  // The dialog can already be open when a create-audio starts: every guard on the
  // way in sits on the menu that opens it, and none of them is re-read here.
  const hasSynthesizingBlock = useSelector(selectHasSynthesizingBlock);
  const [sourceLanguage, setSourceLanguage] = useState('auto');
  const [targetLanguage, setTargetLanguage] = useState('');
  const [targetLanguageError, setTargetLanguageError] = useState(false);

  // Transform blocks to TranslateBlockDto format
  const translateBlocks = useMemo(
    () =>
      blocks
        .filter((block) => block && block.id && block.text) // Filter out invalid blocks
        .map((block) => ({
          blockId: block.id,
          ...(block.startTime && { startTime: block.startTime }),
          ...(block.endTime && { endTime: block.endTime }),
          originalText: block.text,
        })),
    [blocks],
  );

  const translateCredits = useMemo(
    () => calculateTranslateCredits(blocks.map((block) => block.text || '')),
    [blocks],
  );

  const sortedLanguages = useMemo(() => {
    const supportedLanguages = Array.isArray(
      translateSupportedLanguages?.languageCodes,
    )
      ? translateSupportedLanguages.languageCodes
      : [];

    return [...(languagesV3 || [])]
      .filter((item) => item.value !== 'multilingual') // Exclude multilingual language for translation
      .filter(
        (item) =>
          supportedLanguages.length === 0 ||
          supportedLanguages.includes(item.value),
      )
      .sort((a, b) => {
        if (a.rank !== b.rank) return a.rank - b.rank;
        if (currentLang === LANGUAGE.VN) {
          return (a.label?.vi ?? '').localeCompare(b.label?.vi ?? '');
        }
        return (a.label?.en ?? '').localeCompare(b.label?.en ?? '');
      });
  }, [languagesV3, currentLang, translateSupportedLanguages]);

  const languageOptions = useMemo(
    () =>
      sortedLanguages.map((item) => ({
        ...item,
        label: item.label?.[currentLang] || item.label?.[LANGUAGE.EN],
      })),
    [sortedLanguages, currentLang],
  );

  const sourceLanguageOptions = useMemo(
    () => [{ label: t('autoDetect'), value: 'auto' }, ...languageOptions],
    [languageOptions, t],
  );

  const targetLanguageOptions = useMemo(
    () => languageOptions,
    [languageOptions],
  );

  const getSelectedLabel = (options, value) => {
    const option = options.find((item) => item.value === value);
    return option?.label || '';
  };

  const handleClose = () => {
    onClose();
  };

  const handleTranslate = async () => {
    if (hasSynthesizingBlock) return;

    // Check if target language is selected
    if (!targetLanguage) {
      setTargetLanguageError(true);
      return;
    }

    if (totalCredits < translateCredits) {
      onOpenPopupNotEnoughCredits?.();
      return;
    }

    // Check if any block has text error (skip special character validation for translation)
    const blockWithError = findBlockWithError?.({
      skipInvalidCharacters: true,
    });
    if (blockWithError) {
      handleClose();
      const newMap = computeBlockBlackWordsMap();
      setBlockBlackWordsMap(newMap);
      scrollToBlock?.(blockWithError.id);
      return;
    }

    // Close dialog and show loading overlay immediately
    handleClose();
    onTranslateLoadingChange?.(true);

    try {
      // Delete translation first to clear the translation state
      await apis.project.deleteTranslation(projectId);
      dispatch(actions.project.clearTranslation());
      if (undoRedoEnabled) {
        dispatch(actions.history.clear(HISTORY_SCOPE.DUBBING));
      }

      // Save project first before translating
      const saveResponse = await onSaveProject?.();

      if (!saveResponse) {
        // Save failed, stop loading and show error
        onTranslateLoadingChange?.(false);
        return;
      }

      const deleteTranslationResponse = await apis.project.deleteTranslation(
        projectId,
      );
      if (!deleteTranslationResponse?.status) {
        onTranslateLoadingChange?.(false);
        onTranslateError?.(t('translateFailed'));
        return;
      }

      const hasRewriteInBlocks = blocks.some((block) =>
        Boolean(block?.rewrite),
      );
      if (hasRewriteInBlocks) {
        const deleteRewriteResponse = await apis.project.deleteProjectRewrite(
          projectId,
        );
        if (!deleteRewriteResponse?.status) {
          onTranslateLoadingChange?.(false);
          onTranslateError?.(t('translateFailed'));
          return;
        }

        const currentBlocks = multiBlock?.blocks || {};
        const hasRewriteInStore = Object.values(currentBlocks).some((block) =>
          Boolean(block?.rewrite),
        );

        if (hasRewriteInStore) {
          const updatedBlocks = Object.entries(currentBlocks).reduce(
            (acc, [blockId, block]) => {
              acc[blockId] = block?.rewrite
                ? { ...block, rewrite: null }
                : block;
              return acc;
            },
            {},
          );

          dispatch(
            actions.project.setMultiBlock({
              ...multiBlock,
              blocks: updatedBlocks,
            }),
          );
        }
      }

      const response = await handleTranslateProject({
        projectId,
        sourceLanguage,
        targetLanguage,
        blocks: translateBlocks,
      });

      if (response?.status && response?.translationId) {
        // Continue showing loading overlay and let polling decide when to stop
        onTranslateLoadingChange?.(true, response.translationId);
      } else {
        onTranslateLoadingChange?.(false);
        onTranslateError?.(t('translateFailed'));
      }
    } catch (error) {
      onTranslateLoadingChange?.(false);
      onTranslateError?.(t('translateFailed'));
    }
  };

  const renderContent = () => (
    <StyledTranslateContent>
      <div className="language-selectors">
        <div className="language-selector">
          <Typography className="input-label" variant="body-14-medium">
            {t('originalLanguage')}
          </Typography>
          <DropdownCustom
            variant="form-select"
            options={sourceLanguageOptions}
            onChange={(value) => setSourceLanguage(value)}
            value={sourceLanguage}
            searchable
            searchPlaceholder={t('search')}
            title={
              <Typography
                variant="body-14-regular"
                color={sourceLanguage ? COLOR.darkGray : COLOR.label2[48]}
              >
                {getSelectedLabel(sourceLanguageOptions, sourceLanguage) ||
                  t('selectLanguage')}
              </Typography>
            }
          />
        </div>
        <div className="language-selector">
          <Typography className="input-label" variant="body-14-medium">
            {t('translateLanguage')}
          </Typography>
          <div className={targetLanguageError ? 'dropdown-error' : ''}>
            <DropdownCustom
              variant="form-select"
              options={targetLanguageOptions}
              onChange={(value) => {
                setTargetLanguage(value);
                setTargetLanguageError(false);
              }}
              value={targetLanguage}
              searchable
              searchPlaceholder={t('search')}
              title={
                <Typography
                  variant="body-14-regular"
                  color={targetLanguage ? COLOR.darkGray : COLOR.label2[48]}
                >
                  {getSelectedLabel(targetLanguageOptions, targetLanguage) ||
                    t('selectTargetLanguage')}
                </Typography>
              }
            />
          </div>
          {targetLanguageError && (
            <Typography
              variant="body-12-regular"
              color={COLOR.functionalRed.default}
              className="error-message"
            >
              {t('languageNotSelected')}
            </Typography>
          )}
        </div>
      </div>

      <div className="tip-container">
        <TipsAndUpdatesRoundedIcon className="tip-icon" />
        <span
          className="tip-text"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: t('translateTip') }}
        />
      </div>
    </StyledTranslateContent>
  );

  const buttonStyle = {
    height: '40px',
    borderRadius: '8px',
    padding: '8px 24px',
  };

  const renderActions = () => (
    <StyledTranslateActions>
      <CommonButton
        variant="outline"
        onClick={handleClose}
        customStyle={{
          ...buttonStyle,
          border: '1px solid rgba(30, 22, 78, 0.16)',
        }}
      >
        <Typography variant="body-14-medium" color={COLOR.darkBlue}>
          {t('cancel')}
        </Typography>
      </CommonButton>
      <Tooltip
        title={t('costsCredit', { value: translateCredits })}
        arrow
        placement="top"
      >
        <span>
          <CommonButton
            variant="containedSmall"
            onClick={handleTranslate}
            disabled={isAutoSaveOverLimit || hasSynthesizingBlock}
            customStyle={{
              ...buttonStyle,
              border: 'none',
              '&:hover': {
                backgroundColor: COLOR.yellow[64],
              },
            }}
          >
            <Typography variant="body-14-medium" color={COLOR.darkBlue}>
              {t('translate')}
            </Typography>
          </CommonButton>
        </span>
      </Tooltip>
    </StyledTranslateActions>
  );

  return (
    <CommonDialog
      open={open}
      onClose={handleClose}
      title={t('translateBtn')}
      width="512px"
      contentComponent={renderContent()}
      actionComponent={renderActions()}
    />
  );
};

export default TranslateDialog;
